/**
 * #1106 — allow-listed raster photo formats for inline rich-text insert.
 * Covers phone camera (HEIC/JPEG), screenshots (PNG), and common gallery exports.
 * Excludes vector/other `image/*` types (e.g. SVG) that are not phone photos.
 * Keep in sync with server/constants/inlineImageMimeTypes.ts.
 */

export const INLINE_PHOTO_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/tif',
] as const;

export type InlinePhotoMimeType = (typeof INLINE_PHOTO_MIME_TYPES)[number];

const INLINE_PHOTO_MIME_SET = new Set<string>(
  INLINE_PHOTO_MIME_TYPES.map(mime => mime.toLowerCase()),
);

export const INLINE_PHOTO_FILE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'heic',
  'heif',
  'gif',
  'bmp',
  'tiff',
  'tif',
] as const;

const INLINE_PHOTO_EXTENSION_SET = new Set<string>(
  INLINE_PHOTO_FILE_EXTENSIONS,
);

/** HTML `<input accept="…">` for the web inline-image file picker. */
export const INLINE_PHOTO_ACCEPT_ATTR = INLINE_PHOTO_MIME_TYPES.join(',');

export const INLINE_PHOTO_REJECTED_ERROR =
  'Use a photo file (JPEG, PNG, HEIC, WebP, GIF, BMP, or TIFF). Other file types are not supported for inline images.';

export function normalizeInlinePhotoMime(mime: string | undefined): string {
  const lower = (mime || '').trim().toLowerCase();
  if (lower === 'image/jpg') {
    return 'image/jpeg';
  }
  return lower;
}

export function isAllowedInlinePhotoMime(mime: string | undefined): boolean {
  const normalized = normalizeInlinePhotoMime(mime);
  return normalized.length > 0 && INLINE_PHOTO_MIME_SET.has(normalized);
}

export function isAllowedInlinePhotoExtension(
  fileName: string | undefined,
): boolean {
  if (!fileName?.trim()) {
    return false;
  }
  const ext = fileName.trim().toLowerCase().split('.').pop() || '';
  return INLINE_PHOTO_EXTENSION_SET.has(ext);
}

/** MIME first; extension fallback when native/web omit `type`. */
export function isAllowedInlinePhotoFile(file: {
  name: string;
  type?: string;
}): boolean {
  if (isAllowedInlinePhotoMime(file.type)) {
    return true;
  }
  return isAllowedInlinePhotoExtension(file.name);
}

export function inlinePhotoExtensionForMime(mimeType: string): string {
  const normalized = normalizeInlinePhotoMime(mimeType);
  switch (normalized) {
    case 'image/png':
      return 'png';
    case 'image/webp':
      return 'webp';
    case 'image/heic':
      return 'heic';
    case 'image/heif':
      return 'heif';
    case 'image/gif':
      return 'gif';
    case 'image/bmp':
      return 'bmp';
    case 'image/tiff':
    case 'image/tif':
      return 'tiff';
    default:
      return 'jpg';
  }
}
